// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/recorrido/estado.js
//  POR DÓNDE VA CADA ALUMNO EN EL RECORRIDO
//
//  ── POR QUÉ EN EL NAVEGADOR Y NO EN EL SERVIDOR ────────────────────────────
//  Porque no es un dato del negocio: es una preferencia de esta persona en este
//  aparato. Guardarlo en la base obliga a una columna nueva —que en una base ya
//  viva exige migración— y a una llamada más en el arranque del Coach, que ya
//  hace varias.
//
//  El precio de tenerlo aquí está asumido: quien entre desde otro teléfono verá
//  el recorrido otra vez. Y eso, para un recorrido que se puede cerrar en un
//  toque, es mejor que el caso contrario —alguien que lo omite en el móvil y no
//  lo vuelve a encontrar nunca en el ordenador—.
//
//  ── SALIR NO ES PERDER ─────────────────────────────────────────────────────
//  Se guarda el PASO, no sólo un «ya lo vio». Rob: «como sabemos que pueden
//  omitir, lo ideal es crear su botón». Si omite en el paso 3 y mañana pulsa el
//  botón, retoma en el 3. Empezar de cero a quien ya vio tres pantallas es la
//  forma más rápida de que no lo vuelva a abrir.
// ═══════════════════════════════════════════════════════════════════════════════

import { VERSION_DEL_GUION } from './guion';

const LLAVE = 'nsl_recorrido';

/**
 * La llave lleva dentro DE QUIÉN es el recorrido.
 *
 * Rob, 30-09-2026: dio de alta una cuenta nueva desde su navegador de siempre y
 * el Coach la recibió con «¡Ey, ya llegaste!» en vez del saludo de bienvenida,
 * y sin recorrido. Desde una ventana de incógnito funcionó perfecto.
 *
 * La causa es que esto vivía en una llave única del navegador: su «ya lo vi»,
 * de hace semanas y de otra cuenta, contestaba por la alumna recién creada. Y
 * no es un caso de laboratorio — pasa siempre que dos personas comparten un
 * ordenador, que en una casa o en una empresa es lo normal.
 *
 * Con el identificador dentro, cada cuenta tiene el suyo en el mismo aparato.
 * Sin sesión se usa la llave pelada: es el único momento en que no hay a quién
 * atribuirlo, y ahí da igual porque el recorrido vive dentro del Coach.
 */
function llave() {
    try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        const quien = u?.id ?? u?.email;
        return quien ? `${LLAVE}:${quien}` : LLAVE;
    } catch {
        return LLAVE;       // ventana privada o almacenamiento bloqueado
    }
}

const VACIO = {
    version: VERSION_DEL_GUION,
    paso: 0,          // el índice del siguiente paso por ver
    terminado: false,
    omitido: false,
    saludado: false,  // ya pasó de la pantalla de bienvenida
    // ── LO QUE YA HA VISTO, UNO A UNO ──────────────────────────────────────
    //  Rob: «que se marque en amarillo como si fuera visto o aprendido, así
    //  evitamos el doble clic».
    //
    //  No basta con el índice de `paso`: desde el menú se puede saltar a la
    //  séptima función sin haber visto las seis anteriores, así que «he
    //  llegado al 7» no significa «he visto siete». Hay que guardar cuáles.
    vistos: [],
};

export function leeRecorrido() {
    try {
        const crudo = localStorage.getItem(llave());
        if (!crudo) return { ...VACIO };
        const guardado = JSON.parse(crudo);
        // Guion distinto = progreso sin sentido. Retomar en el paso 5 de un
        // orden que ya no existe deja al alumno en una explicación que no es la
        // que esperaba, y eso es peor que volver a empezar.
        if (guardado?.version !== VERSION_DEL_GUION) return { ...VACIO };
        const e = { ...VACIO, ...guardado };
        // Lo guardado antes de que existiera `vistos` no lo trae. Se repara al
        // leer y no en cada sitio que lo use.
        if (!Array.isArray(e.vistos)) e.vistos = [];
        return e;
    } catch {
        return { ...VACIO };   // ventana privada o almacenamiento bloqueado
    }
}

function guarda(estado) {
    try {
        localStorage.setItem(llave(), JSON.stringify(estado));
        // La llave vieja, sin dueño, se retira en cuanto hay una con nombre: si
        // se queda, el día que alguien entre sin sesión vuelve a contestar por
        // una cuenta que no es la suya. No se MIGRA a propósito — migrarla es
        // exactamente el fallo que se está arreglando, sólo que una vez más.
        const propia = llave();
        if (propia !== LLAVE) localStorage.removeItem(LLAVE);
    } catch { /* sin almacenamiento, el recorrido dura lo que la pestaña */ }
    return estado;
}

/** ¿Le toca ver el recorrido nada más entrar? */
export function tocaRecorrido() {
    const e = leeRecorrido();
    return !e.terminado && !e.omitido;
}

/**
 * ¿Ya ha visto el recorrido ENTERO?
 *
 * No es lo contrario de `tocaRecorrido()`, y la diferencia importa. Quien lo
 * OMITE deja de tenerlo delante —`tocaRecorrido()` pasa a ser false— pero sigue
 * sin saber qué hay en el gimnasio, y es justo la persona a la que el Coach
 * tiene que recibir explicándole la casa en vez de preguntarle «¿por dónde le
 * entramos?».
 *
 * Por eso el saludo de bienvenida se decide con ESTA y no con la otra: la otra
 * sólo es true cuando el recorrido está en pantalla, momento en el que el Coach
 * todavía no ha saludado a nadie.
 */
export const haVistoElRecorrido = () => leeRecorrido().terminado;

export const marcaSaludado = () => guarda({ ...leeRecorrido(), saludado: true });

export const guardaPaso = (paso) => {
    const e = leeRecorrido();
    const vistos = e.vistos.includes(paso) ? e.vistos : [...e.vistos, paso];
    return guarda({ ...e, paso, vistos, saludado: true });
};

export const marcaTerminado = () =>
    guarda({ ...leeRecorrido(), terminado: true, omitido: false });

/**
 * Omitir NO borra el progreso: guarda dónde se quedó. El botón «Cómo se
 * entrena aquí» lo retoma justo ahí.
 */
export const marcaOmitido = (paso) =>
    guarda({ ...leeRecorrido(), omitido: true, paso });

/** Para volver a verlo entero desde el menú. */
/**
 * Volver a verlo entero. Borra TAMBIÉN las marcas de visto: si se queda el
 * amarillo puesto, «verlo otra vez» empieza con las once fichas diciendo que
 * ya las viste, que es justo lo contrario de lo que el alumno acaba de pedir.
 */
export const reinicia = () => guarda({ ...VACIO, vistos: [], saludado: true });
